import myLightRight from "../../assets/light-section.svg";
import myLightLeft from "../../assets/Light-section-left.svg";
import sendIcon from "../../assets/Send.svg";
import { Shield, User, Building2, Briefcase, ChevronDown, Check } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import emailjs from "@emailjs/browser";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { sendGenerateLeadEvent } from "@/lib/analytics";

const LetsConnect = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    plan: "",
    company: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isPlanDropdownOpen, setIsPlanDropdownOpen] = useState(false);
  const planDropdownRef = useRef<HTMLDivElement>(null);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const EMAILJS_PUBLIC_KEY = import.meta.env.VITE_EMAILJS_PUBLIC_KEY as string;
  const EMAILJS_SERVICE_ID = import.meta.env.VITE_EMAILJS_SERVICE_ID as string;
  const EMAILJS_TEMPLATE_ID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID as string;

  const getPlanLabel = (planValue: string): string => {
    const planMap: Record<string, string> = {
      individual: "Individual Plan",
      business: "Business Plan",
      enterprise: "Enterprise Plan",
    };
    return planMap[planValue] || planValue;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate plan is selected
    if (!formData.plan) {
      setIsPlanDropdownOpen(true);
      return;
    }
    
    setIsSubmitting(true);

    try {
      if (!executeRecaptcha) {
        console.error("reCAPTCHA not loaded");
        setIsSubmitting(false);
        return;
      }

      const token = await executeRecaptcha("contact_form");
      if (!token) {
        console.error("reCAPTCHA token not received");
        setIsSubmitting(false);
        return;
      }

      emailjs.init(EMAILJS_PUBLIC_KEY);

      const templateParams = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        plan: getPlanLabel(formData.plan),
        company: formData.company,
        message: formData.message,
        recaptcha: token,
      };

      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);

      const searchParams = new URLSearchParams(window.location.search);
      sendGenerateLeadEvent({
        plan_selected: getPlanLabel(formData.plan),
        utm_source: searchParams.get("utm_source"),
        utm_medium: searchParams.get("utm_medium"),
        utm_campaign: searchParams.get("utm_campaign"),
      });

      setSubmitted(true);

      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        plan: "",
        company: "",
        message: "",
      });
      setIsPlanDropdownOpen(false);
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePlanSelect = (planValue: string) => {
    setFormData({
      ...formData,
      plan: planValue,
    });
    setIsPlanDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (planDropdownRef.current && !planDropdownRef.current.contains(event.target as Node)) {
        setIsPlanDropdownOpen(false);
      }
    };

    if (isPlanDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isPlanDropdownOpen]);

  const planOptions = [
    {
      value: "individual",
      label: "Individual Plan",
      icon: User,
      description: "For Individuals and Executives",
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      value: "business",
      label: "Business Plan",
      icon: Building2,
      description: "For SMBs and Researchers",
      gradient: "from-green-500 to-emerald-500",
    },
    {
      value: "enterprise",
      label: "Enterprise Plan",
      icon: Briefcase,
      description: "For Large Enterprises and Governments",
      gradient: "from-purple-500 to-pink-500",
    },
  ];

  const selectedPlan = planOptions.find((plan) => plan.value === formData.plan);

  if (submitted) {
    return (
      <div className="min-h-screen bg-background-light dark:bg-background-dark flex items-center justify-center">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-md shadow-green-500/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-3xl font-grotesk font-bold text-text-primary-light dark:text-text-primary-dark mb-4">
            Thank you for reaching out!
          </h1>
          <p className="text-xl text-text-secondary-light dark:text-text-secondary-dark mb-8">
            We've received your message and will get back to you within 24 hours.
          </p>
          <button
            onClick={() => {
              setSubmitted(false);
              setFormData({
                firstName: "",
                lastName: "",
                email: "",
                phone: "",
                plan: "",
                company: "",
                message: "",
              });
            }}
            className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-md shadow-green-500/30 transition-all duration-200 rounded-full"
          >
            Send Another Message
          </button>
        </div>
      </div>
    );
  }

  return (
    <section className="relative bg-black text-white px-6 py-24 z-10">
      <div
        className="container mx-auto max-w-6xl z-50 bg-transparent"
        id="contact-us"
      >
        {/* Section badge and title */}
        <div className="flex flex-col items-center text-center mb-16">
          <div className="mb-6 bg-emerald-950/50 text-emerald-400 border border-emerald-500/30 rounded-full px-4 py-2 text-sm flex items-center">
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            Let's Connect
          </div>
          <div className="w-full flex justify-center items-center">
            {/* Your existing SVG */}
          </div>
          <p className="text-gray-400 max-w-3xl text-lg">
            We're here to help you stay ahead of cyber threats. Get in touch to
            learn more about ThreatInformat or to discuss your organization's
            specific security needs.
          </p>
        </div>

        {/* Contact form and info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Contact form */}
          <div className="rounded-2xl p-8">
            <h3 className="text-2xl font-bold mb-6">Get in Touch</h3>
            <p className="text-gray-400 mb-8">
              Fill out the form below and we'll get back to you as soon as
              possible.
            </p>

            <form
              onSubmit={handleSubmit}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label
                    className="block text-gray-300 text-sm font-medium mb-2"
                    htmlFor="firstName"
                  >
                    First Name*
                  </label>
                  <input
                    className="w-full bg-[#6BBE891A] rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="First name"
                    required
                  />
                </div>
                <div>
                  <label
                    className="block text-gray-300 text-sm font-medium mb-2"
                    htmlFor="lastName"
                  >
                    Last Name*
                  </label>
                  <input
                    className="w-full bg-[#6BBE891A] rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Last name"
                    required
                  />
                </div>
              </div>
              <div className="mb-6">
                <label
                  className="block text-gray-300 text-sm font-medium mb-2"
                  htmlFor="email"
                >
                  Email*
                </label>
                <input
                  className="w-full bg-[#6BBE891A] rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your.email@company.com"
                  required
                />
              </div>
              <div className="mb-6">
                <label
                  className="block text-gray-300 text-sm font-medium mb-2"
                  htmlFor="phone"
                >
                  Phone Number*
                </label>
                <input
                  className="w-full bg-[#6BBE891A] rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1 234 777 2000"
                  required
                />
              </div>
              <div className="mb-6">
                <label
                  className="block text-gray-300 text-sm font-medium mb-2"
                  htmlFor="plan"
                >
                  Plan*
                </label>
                <div className="relative" ref={planDropdownRef}>
                  {/* Hidden input for form submission */}
                  <input
                    type="hidden"
                    name="plan"
                    value={formData.plan}
                    required
                  />
                  
                  {/* Custom Dropdown Button */}
                  <button
                    type="button"
                    onClick={() => setIsPlanDropdownOpen(!isPlanDropdownOpen)}
                    className={`
                      w-full bg-[#6BBE891A] rounded-lg py-3 px-4 
                      text-white focus:outline-none 
                      border-2 transition-all duration-300
                      flex items-center justify-between
                      relative overflow-hidden
                      ${isPlanDropdownOpen 
                        ? 'border-green-500 ring-2 ring-green-500/50 shadow-lg shadow-green-500/30' 
                        : !formData.plan
                        ? 'border-gray-600/50 hover:border-green-500/50 hover:shadow-md hover:shadow-green-500/10'
                        : 'border-green-500/30 hover:border-green-500/50 hover:shadow-md hover:shadow-green-500/10'
                      }
                      ${!formData.plan ? 'text-gray-400' : 'text-white'}
                      group
                    `}
                  >
                    {/* Animated background gradient on hover */}
                    <div className="absolute inset-0 bg-gradient-to-r from-green-500/0 via-green-500/5 to-green-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    
                    <div className="flex items-center gap-3 relative z-10 flex-1">
                      {selectedPlan ? (
                        <>
                          <div className={`p-1.5 rounded-lg bg-gradient-to-r ${selectedPlan.gradient} shadow-md ring-1 ring-white/20 transition-transform duration-200 group-hover:scale-110`}>
                            <selectedPlan.icon className="w-4 h-4 text-white" />
                          </div>
                          <div className="text-left flex-1">
                            <div className="text-sm font-medium text-white flex items-center gap-2">
                              {selectedPlan.label}
                              <Check className="w-3.5 h-3.5 text-green-400" />
                            </div>
                            <div className="text-xs text-gray-400">
                              {selectedPlan.description}
                            </div>
                          </div>
                        </>
                      ) : (
                        <span className="text-gray-400">Select a plan</span>
                      )}
                    </div>
                    <ChevronDown 
                      className={`w-5 h-5 text-gray-400 transition-all duration-300 relative z-10 ${
                        isPlanDropdownOpen 
                          ? 'transform rotate-180 text-green-400' 
                          : 'group-hover:text-green-400'
                      }`} 
                    />
                  </button>

                  {/* Dropdown Options */}
                  {isPlanDropdownOpen && (
                    <div className="absolute z-50 w-full mt-2 bg-[#1a1a1a] border border-green-500/30 rounded-lg shadow-2xl overflow-hidden backdrop-blur-sm animate-slide-down ring-1 ring-green-500/20">
                      <div className="py-2">
                        {planOptions.map((plan) => {
                          const Icon = plan.icon;
                          const isSelected = formData.plan === plan.value;
                          
                          return (
                            <button
                              key={plan.value}
                              type="button"
                              onClick={() => handlePlanSelect(plan.value)}
                              className={`
                                w-full px-4 py-3 flex items-center gap-3
                                transition-all duration-200 group relative
                                hover:bg-[#6BBE891A] hover:border-l-4
                                border-l-4 overflow-hidden
                                ${
                                  isSelected
                                    ? 'bg-[#6BBE891A] border-l-green-500'
                                    : 'border-l-transparent hover:border-l-green-500/50'
                                }
                              `}
                            >
                              {/* Hover glow effect */}
                              <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                              
                              <div className={`
                                p-2 rounded-lg bg-gradient-to-r ${plan.gradient}
                                shadow-lg transition-all duration-200 relative z-10
                                ${isSelected ? 'ring-2 ring-green-500 ring-offset-2 ring-offset-[#1a1a1a] scale-110 shadow-green-500/50' : 'hover:scale-105 hover:shadow-xl'}
                              `}>
                                <Icon className="w-4 h-4 text-white" />
                              </div>
                              <div className="flex-1 text-left relative z-10">
                                <div className="flex items-center gap-2">
                                  <span className={`text-sm font-medium transition-colors duration-200 ${
                                    isSelected ? 'text-white' : 'text-white group-hover:text-green-300'
                                  }`}>
                                    {plan.label}
                                  </span>
                                  {isSelected && (
                                    <Check className="w-4 h-4 text-green-400" />
                                  )}
                                </div>
                                <div className={`text-xs mt-0.5 transition-colors duration-200 ${
                                  isSelected ? 'text-gray-300' : 'text-gray-400 group-hover:text-gray-300'
                                }`}>
                                  {plan.description}
                                </div>
                              </div>
                              {isSelected && (
                                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse relative z-10 shadow-lg shadow-green-500/50"></div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="mb-6">
                <label
                  className="block text-gray-300 text-sm font-medium mb-2"
                  htmlFor="company"
                >
                  Company*
                </label>
                <input
                  className="w-full bg-[#6BBE891A] rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Your company name"
                  required
                />
              </div>
              <div className="mb-6">
                <label
                  className="block text-gray-300 text-sm font-medium mb-2"
                  htmlFor="message"
                >
                  How can we help you?*
                </label>
                <textarea
                  className="w-full bg-[#6BBE891A] rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  id="message"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us about your security needs, questions about ThreatInformat, or how we can help your organization..."
                  required
                ></textarea>
              </div>
              <div className="mb-6 text-gray-400 text-sm">
                By submitting this form, you agree to our{" "}
                <a href="#" className="text-[#05B6B6] hover:underline">
                  Privacy Policy
                </a>
                .
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-all flex items-center justify-center"
              >
                {isSubmitting ? (
                  <span>Submitting...</span>
                ) : (
                  <>
                    <img src={sendIcon} className="w-4 h-4 mr-2" alt="Send" />
                    Submit
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Your existing contact info section remains the same */}
          <div className="space-y-8">
            {/* ... rest of your contact info JSX ... */}
          </div>
        </div>
      </div>
      <img
        src={myLightRight}
        className="absolute -top-[10rem] -right-2 z-[-1]"
        alt="Logo"
        width={600}
        height={600}
      />
      <img
        src={myLightLeft}
        className="absolute top-[5rem] -left-2 z-[-1]"
        alt="Logo"
        width={600}
        height={600}
      />
    </section>
  );
};

export default LetsConnect;
